import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  TextField,
  MenuItem,
  CircularProgress,
  InputAdornment,
  LinearProgress,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Fab,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Snackbar,
  Skeleton,
  Badge,
} from "@mui/material";
import {
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Upload as UploadIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon,
  Archive as ArchiveIcon,
  Verified as VerifiedIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

// Import type definitions for JSDoc
import "../../types/api/patient";
import "../../types/api/appointments";
import "../../types/api/messages";
import "../../types/api/feedback";

// Import API services
import { patientApi } from "../../api/patientApi";
import axiosInstance from "../../api/axiosInstance";

// Import components
import DocumentViewer from "../../components/DocumentViewer";
import FileUploadDialog from "../../components/FileUploadDialog";
import DocumentFilters from "../../components/DocumentFilters";

const MedicalRecords = () => {
  const dispatch = useDispatch();
  
  // State management
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    type: "",
    dateFrom: "",
    dateTo: "",
    verifiedOnly: false,
  });
  
  // Dialog states
  const [viewerOpen, setViewerOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [filtersDialogOpen, setFiltersDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [hasMore, setHasMore] = useState(true);

  // Fetch medical records summary
  const fetchSummary = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/patient/documents/summary/');
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
      let errorMessage = 'Failed to load medical records summary';
      
      if (error.response?.status === 404) {
        errorMessage = 'Summary not available';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to view the summary';
      } else if (error.code === 'NETWORK_ERROR') {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Request timeout. Please try again.';
      }
      
      toast.error(errorMessage);
    }
  }, []);

  // Fetch documents with filters
  const fetchDocuments = useCallback(async (resetPage = false) => {
    try {
      setLoading(true);
      const currentPage = resetPage ? 1 : page;
      
      const params = {
        page: currentPage,
        page_size: pageSize,
        ...filters,
      };
      
      const response = await axiosInstance.get('/patient/documents/', { params });
      
      if (resetPage) {
        setDocuments(response.data.results || response.data);
        setPage(1);
      } else {
        setDocuments(prev => [...prev, ...(response.data.results || response.data)]);
      }
      
      setHasMore((response.data.results || response.data).length === pageSize);
      
      if (resetPage) {
        setPage(1);
      } else {
        setPage(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      let errorMessage = 'Failed to load medical records';
      
      if (error.response?.status === 404) {
        errorMessage = 'No documents found';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to view these documents';
      } else if (error.code === 'NETWORK_ERROR') {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Request timeout. Please try again.';
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  // Load more documents
  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      fetchDocuments(false);
    }
  }, [loading, hasMore, fetchDocuments]);

  // Handle document upload
  const handleUpload = useCallback(async (uploadData) => {
    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('document', uploadData.file);
      formData.append('title', uploadData.title);
      formData.append('description', uploadData.description || '');
      formData.append('document_type', uploadData.type);
      formData.append('visibility', uploadData.visibility);
      formData.append('is_patient_visible', uploadData.isPatientVisible);
      
      if (uploadData.doctorId) {
        formData.append('doctor_id', uploadData.doctorId);
      }
      
      if (uploadData.appointmentId) {
        formData.append('appointment_id', uploadData.appointmentId);
      }
      
      // Generate idempotency key to prevent duplicate uploads
      const idempotencyKey = `${uploadData.file.name}_${uploadData.title}_${Date.now()}`;
      
      const response = await axiosInstance.post('/patient/documents/upload/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Idempotency-Key': idempotencyKey,
        },
      });
      
      toast.success('Document uploaded successfully');
      setUploadDialogOpen(false);
      
      // Refresh documents and summary
      fetchDocuments(true);
      fetchSummary();
      
    } catch (error) {
      console.error('Error uploading document:', error);
      let errorMessage = 'Failed to upload document';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.patient) {
          errorMessage = `Patient error: ${error.response.data.patient.join(', ')}`;
        } else if (error.response.data.document) {
          errorMessage = `Document error: ${error.response.data.document.join(', ')}`;
        } else if (error.response.data.document_type) {
          errorMessage = `Document type error: ${error.response.data.document_type.join(', ')}`;
        } else if (error.response.data.visibility) {
          errorMessage = `Visibility error: ${error.response.data.visibility.join(', ')}`;
        } else {
          errorMessage = Object.values(error.response.data).flat().join(', ');
        }
      } else if (error.code === 'NETWORK_ERROR') {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Request timeout. Please try again.';
      }
      
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  }, [fetchDocuments, fetchSummary]);

  // Handle document download
  const handleDownload = useCallback(async (doc) => {
    try {
      const response = await axiosInstance.get(`/patient/documents/${doc.id}/download/`, {
        responseType: 'blob',
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.title || 'document');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Document downloaded successfully');
    } catch (error) {
      console.error('Error downloading document:', error);
      let errorMessage = 'Failed to download document';
      
      if (error.response?.status === 404) {
        errorMessage = 'Document not found or no longer available';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to download this document';
      } else if (error.code === 'NETWORK_ERROR') {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Download timeout. Please try again.';
      }
      
      toast.error(errorMessage);
    }
  }, []);

  // Handle document view
  const handleView = useCallback((document) => {
    setSelectedDocument(document);
    setViewerOpen(true);
  }, []);

  // Handle filters change
  const handleFiltersChange = useCallback((newFilters) => {
    setFilters(newFilters);
    fetchDocuments(true);
  }, [fetchDocuments]);

  // Clear filters
  const clearFilters = useCallback(() => {
    setFilters({
      search: "",
      type: "",
      dateFrom: "",
      dateTo: "",
      verifiedOnly: false,
    });
    fetchDocuments(true);
  }, [fetchDocuments]);

  // Get document icon based on type
  const getDocumentIcon = useCallback((mimeType, documentType) => {
    if (mimeType?.includes('pdf')) return <PdfIcon />;
    if (mimeType?.includes('image')) return <ImageIcon />;
    if (mimeType?.includes('video')) return <VideoIcon />;
    if (mimeType?.includes('zip') || mimeType?.includes('rar')) return <ArchiveIcon />;
    return <DescriptionIcon />;
  }, []);

  // Get document type color
  const getDocumentTypeColor = useCallback((type) => {
    const colors = {
      prescription: 'primary',
      lab_result: 'secondary',
      consultation_note: 'info',
      imaging: 'warning',
      medical_certificate: 'success',
      vaccination_record: 'error',
      insurance_card: 'default',
      identity_proof: 'default',
      other: 'default',
    };
    return colors[type] || 'default';
  }, []);

  // Format file size
  const formatFileSize = useCallback((bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  // Initial load
  useEffect(() => {
    fetchSummary();
    fetchDocuments(true);
  }, [fetchSummary, fetchDocuments]);

  return (
    <Box sx={{ padding: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold", color: "#007BFF" }}>
          Medical Records
        </Typography>
        
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setFiltersDialogOpen(true)}
          >
            Filters
          </Button>
          
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => setUploadDialogOpen(true)}
          >
            Upload Document
          </Button>
        </Stack>
      </Box>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#007BFF' }}>
                      {summary.total_documents}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Documents
                    </Typography>
                  </Box>
                  <DescriptionIcon sx={{ fontSize: 40, color: '#007BFF' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#28a745' }}>
                      {summary.storage_used_mb}MB
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Storage Used
                    </Typography>
                  </Box>
                  <StorageIcon sx={{ fontSize: 40, color: '#28a745' }} />
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(summary.storage_used_mb / summary.storage_limit_mb) * 100}
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ffc107' }}>
                      {Object.keys(summary.documents_by_type || {}).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Document Types
                    </Typography>
                  </Box>
                  <ArchiveIcon sx={{ fontSize: 40, color: '#ffc107' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#dc3545' }}>
                      {summary.last_upload ? format(parseISO(summary.last_upload), 'MMM dd') : 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Last Upload
                    </Typography>
                  </Box>
                  <CalendarIcon sx={{ fontSize: 40, color: '#dc3545' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Search and Filters */}
      <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search documents by title or description..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                endAdornment: filters.search && (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setFilters(prev => ({ ...prev, search: '' }))}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Document Type</InputLabel>
              <Select
                value={filters.type}
                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                label="Document Type"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="prescription">Prescription</MenuItem>
                <MenuItem value="lab_result">Lab Result</MenuItem>
                <MenuItem value="consultation_note">Consultation Note</MenuItem>
                <MenuItem value="imaging">Imaging Report</MenuItem>
                <MenuItem value="medical_certificate">Medical Certificate</MenuItem>
                <MenuItem value="vaccination_record">Vaccination Record</MenuItem>
                <MenuItem value="insurance_card">Insurance Card</MenuItem>
                <MenuItem value="identity_proof">Identity Proof</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => fetchDocuments(true)}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : 'Search'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Documents Grid */}
      <Paper elevation={3} sx={{ padding: 2, borderRadius: 2, backgroundColor: "#F9F9F9" }}>
        {loading && documents.length === 0 ? (
          <Grid container spacing={2}>
            {[...Array(6)].map((_, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
        ) : documents.length > 0 ? (
          <Grid container spacing={2}>
            {documents.map((document) => (
              <Grid item xs={12} md={6} lg={4} key={document.id}>
                <Card sx={{ 
                  borderRadius: "12px", 
                  boxShadow: 2,
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}>
                  <CardContent>
                    <Stack spacing={2}>
                      {/* Document Header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getDocumentIcon(document.mime_type, document.document_type)}
                          <Typography variant="h6" sx={{ fontWeight: "bold", color: "#007BFF" }}>
                            {document.title}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                          {document.is_verified && (
                            <Tooltip title="Verified">
                              <VerifiedIcon color="success" fontSize="small" />
                            </Tooltip>
                          )}
                          <Chip
                            label={document.document_type.replace('_', ' ').toUpperCase()}
                            color={getDocumentTypeColor(document.document_type)}
                            size="small"
                          />
                        </Stack>
                      </Box>

                      {/* Document Info */}
                      <Stack spacing={1}>
                        {document.description && (
                          <Typography variant="body2" color="text.secondary">
                            {document.description}
                          </Typography>
                        )}
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {format(parseISO(document.created_at), 'MMM dd, yyyy')}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <StorageIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {formatFileSize(document.file_size)}
                          </Typography>
                        </Box>
                        
                        {document.doctor_name && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              Dr. {document.doctor_name}
                            </Typography>
                          </Box>
                        )}
                      </Stack>

                      {/* Visibility Badge */}
                      <Chip
                        icon={<SecurityIcon />}
                        label={document.visibility.replace('_', ' ').toUpperCase()}
                        size="small"
                        variant="outlined"
                        color="default"
                      />
                    </Stack>
                  </CardContent>
                  
                  <CardActions sx={{ justifyContent: "space-between", px: 2, pb: 2 }}>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="View Document">
                        <IconButton 
                          onClick={() => handleView(document)} 
                          color="primary"
                          size="small"
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Download">
                        <IconButton 
                          onClick={() => handleDownload(document)} 
                          color="secondary"
                          size="small"
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    
                    <Typography variant="body2" color="text.secondary">
                      {document.file_size_mb}MB
                    </Typography>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <DescriptionIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No documents found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {filters.search || filters.type ? 
                'Try adjusting your search criteria' : 
                'Upload your first medical document to get started'
              }
            </Typography>
            {!filters.search && !filters.type && (
              <Button
                variant="contained"
                startIcon={<CloudUploadIcon />}
                onClick={() => setUploadDialogOpen(true)}
              >
                Upload Document
              </Button>
            )}
          </Box>
        )}

        {/* Load More */}
        {hasMore && documents.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Button 
              variant="outlined" 
              onClick={loadMore}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : <AddIcon />}
            >
              {loading ? 'Loading...' : 'Load More'}
            </Button>
          </Box>
        )}
      </Paper>

      {/* Document Viewer Dialog */}
      <DocumentViewer
        open={viewerOpen}
        document={selectedDocument}
        onClose={() => {
          setViewerOpen(false);
          setSelectedDocument(null);
        }}
      />

      {/* File Upload Dialog */}
      <FileUploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onUpload={handleUpload}
        uploading={uploading}
      />

      {/* Filters Dialog */}
      <DocumentFilters
        open={filtersDialogOpen}
        filters={filters}
        onClose={() => setFiltersDialogOpen(false)}
        onApply={handleFiltersChange}
        onClear={clearFilters}
      />

      {/* Floating Upload Button */}
      <Fab
        color="primary"
        aria-label="upload document"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => setUploadDialogOpen(true)}
      >
        <UploadIcon />
      </Fab>
    </Box>
  );
};

export default MedicalRecords;
